import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type AuditVariant = {
  traits: string[];
  students: number;
  sample: string[];
  listed?: number;
};

export type AuditTeacherView = {
  admins: number;
  teachers: number;
  checked: number;
  unanswered: number;
  empty: number;
  fewest: number;
  median: number;
  most: number;
};

/** How a district's students differ in the data the teacher pages are built from; an absent field is one the audit could not find out. */
export type AuditVariance = {
  students: number;
  plansGenerated?: boolean;
  onFlashlightRoster?: number;
  data: AuditVariant[];
  report: AuditVariant[];
  plan: AuditVariant[];
  gaps: Record<string, number>;
  users?: AuditTeacherView;
};

export type AuditFeedVerdict = {
  present: boolean;
  used: boolean;
  rows: number;
  refusal?: string;
  matched: number;
  tested: number;
  schoolYear?: string;
  empty?: boolean;
  csvRosterUsed?: boolean;
  /** Uploaded roster students the feed was found to list, and whether it covered enough of them to be the roster of record. */
  csvRows?: number;
  csvLinked?: number;
  rosterOfRecord?: boolean;
  sharedColumns?: AuditSharedColumn[];
  placeholderNames?: AuditPlaceholderName[];
  placeholderStudents?: number;
};

export type AuditSharedColumn = { column: string; fields: string[]; labels: string[] };

export type AuditPlaceholderName = { value: string; students: number };

/** The roster columns the district's current uploads name, by the ingest's field keys, and how many rows filled each named one. */
export type AuditRosterColumns = {
  rows: number;
  named: string[];
  missing: string[];
  filled: Record<string, number>;
};

export type CoverageSource = 'feed' | 'upload' | 'wida' | 'absent' | 'empty';

export type AuditFieldCoverage = { filled: number; source: CoverageSource };

export type AuditCoverage = { fields: Record<string, AuditFieldCoverage> };

export type AuditVerdict = 'ready' | 'check' | 'empty' | 'unanswered';

export type AuditStudentProblem = { key: string; reasons: string[] };

export type AuditTeacherResult = {
  sub: string;
  name: string;
  schools: string[];
  students: number;
  currentBenchmark: number;
  /** The newest window across the teacher's own table, which is what their list page counts against. */
  currentWindow?: string;
  /** Students Flashlight 360 lists in the teacher's classes; absent when it could not say. */
  flashlightStudents?: number;
  /** Every problem student is counted; `problems` holds a bounded sample of them. */
  problemStudents: number;
  problems: AuditStudentProblem[];
  verdict: AuditVerdict;
  error?: string;
};

export type AuditTeacherReadiness = {
  currentWindow?: string;
  teachers: AuditTeacherResult[];
  ready: number;
  check: number;
  empty: number;
  unanswered: number;
};

export type AuditFlashlightRefusal = { rows: number; linked: number };

export type AuditDistrict = {
  districtId: string;
  districtName: string;
  audited: boolean;
  auditedAt?: string;
  skippedAt?: string;
  uploads: number;
  students: number;
  /** Students each source holds; a student in two sources counts in both. Absent on a row audited before they were counted. */
  fromUploads?: number;
  fromRosterStream?: number;
  fromFlashlight?: number;
  flashlightRefusal?: AuditFlashlightRefusal;
  attempted: number;
  resolved: number;
  notFound: number;
  foundByAltKey: number;
  flashlightStudents?: number;
  /** Students current or dropped from the roster; absent when the resolver does not count those. */
  flashlightStudentsAll?: number;
  /** Distinct Flashlight 360 students behind the matched rows, and the part of them still on the roster. */
  flashlightMatched?: number;
  flashlightMatchedActive?: number;
  flashlightMatchedFeed?: number;
  flashlightName?: string;
  feedBound?: boolean;
  trial?: boolean;
  /** The district's state as Flashlight 360 records it, whether it is a WIDA consortium state, and whether the resolver holds plans for it; absent when the run could not tell. */
  state?: string;
  wida?: boolean;
  configured?: boolean;
  rosterStream?: AuditFeedVerdict;
  rosterColumns?: AuditRosterColumns;
  rosterStreamColumns?: AuditRosterColumns;
  coverage?: AuditCoverage;
  lastUploadedAt?: string;
  errored: number;
  unattempted: number;
  ambiguous: number;
  failed: number;
  sources: Record<string, number>;
  batches: number;
  failedBatches: number;
  error?: string;
  notFoundSample: string[];
  elapsedMs: number;
  variance?: AuditVariance;
  readiness?: AuditTeacherReadiness;
  part?: AuditPart;
};

export type AuditPart = { of: number; landed: number[]; rosters: string[]; students: boolean; graded: boolean };

export type AuditTotals = {
  districts: number;
  unreached: number;
  students: number;
  attempted: number;
  resolved: number;
  notFound: number;
  foundByAltKey: number;
  flashlightStudents: number;
  flashlightCounted: number;
  flashlightMatchedActive: number;
  flashlightMatchedActiveOf: number;
  flashlightMatchedActiveCounted: number;
  flashlightMatched: number;
  flashlightMatchedOf: number;
  flashlightMatchedCounted: number;
  errored: number;
  unattempted: number;
  ambiguous: number;
  failed: number;
  sources: Record<string, number>;
};

export type AuditReport = {
  computed: boolean;
  kind?: string;
  runAt?: string;
  trigger?: string;
  incomplete?: boolean;
  usersUnknown?: boolean;
  configsUnknown?: boolean;
  districts: AuditDistrict[];
  totals: AuditTotals;
  elapsedMs?: number;
};

type RawVariant = { traits?: (string | null)[] | null; students?: number | null; sample?: (string | null)[] | null; listed?: number | null };
type RawVariance = {
  students?: number | null;
  plansGenerated?: boolean | null;
  onFlashlightRoster?: number | null;
  data?: (RawVariant | null)[] | null;
  report?: (RawVariant | null)[] | null;
  plan?: (RawVariant | null)[] | null;
  gaps?: Record<string, number> | null;
  users?: Partial<AuditTeacherView> | null;
};
type RawProblem = { key?: string | null; reasons?: (string | null)[] | null };
type RawTeacher = {
  sub?: string | null;
  name?: string | null;
  schools?: (string | null)[] | null;
  students?: number | null;
  currentBenchmark?: number | null;
  currentWindow?: string | null;
  flashlightStudents?: number | null;
  problemStudents?: number | null;
  problems?: (RawProblem | null)[] | null;
  verdict?: string | null;
  error?: string | null;
};
type RawReadiness = {
  currentWindow?: string | null;
  teachers?: (RawTeacher | null)[] | null;
  ready?: number | null;
  check?: number | null;
  empty?: number | null;
  unanswered?: number | null;
};
type RawColumns = { rows?: number | null; named?: (string | null)[] | null; missing?: (string | null)[] | null; filled?: Record<string, number> | null };
type RawCoverage = { fields?: Record<string, { filled?: number | null; source?: string | null } | null> | null };
type RawPart = { of?: number | null; landed?: (number | null)[] | null; rosters?: (string | null)[] | null; students?: boolean | null; graded?: boolean | null };
type RawDistrict = Partial<Omit<AuditDistrict, 'flashlightStudents' | 'flashlightStudentsAll' | 'flashlightMatched' | 'flashlightMatchedActive' | 'flashlightMatchedFeed' | 'flashlightName' | 'feedBound' | 'trial' | 'variance' | 'readiness' | 'fromUploads' | 'fromRosterStream' | 'fromFlashlight' | 'flashlightRefusal' | 'state' | 'wida' | 'configured' | 'rosterStream' | 'rosterColumns' | 'rosterStreamColumns' | 'coverage' | 'lastUploadedAt' | 'part'>> & {
  part?: RawPart | null;
  notFoundSample?: (string | null)[] | null;
  state?: string | null;
  wida?: boolean | null;
  configured?: boolean | null;
  rosterStream?: Partial<AuditFeedVerdict> | null;
  rosterColumns?: RawColumns | null;
  rosterStreamColumns?: RawColumns | null;
  coverage?: RawCoverage | null;
  lastUploadedAt?: string | null;
  flashlightStudents?: number | null;
  flashlightStudentsAll?: number | null;
  flashlightMatched?: number | null;
  flashlightMatchedActive?: number | null;
  flashlightMatchedFeed?: number | null;
  flashlightName?: string | null;
  feedBound?: boolean | null;
  trial?: boolean | null;
  fromUploads?: number | null;
  fromRosterStream?: number | null;
  fromFlashlight?: number | null;
  flashlightRefusal?: Partial<AuditFlashlightRefusal> | null;
  variance?: RawVariance | null;
  readiness?: RawReadiness | null;
};
type RawReport = Partial<Omit<AuditReport, 'districts' | 'totals'>> & {
  districts?: (RawDistrict | null)[] | null;
  totals?: Partial<AuditTotals> | null;
};

const EMPTY_TOTALS: AuditTotals = {
  districts: 0,
  unreached: 0,
  students: 0,
  attempted: 0,
  resolved: 0,
  notFound: 0,
  foundByAltKey: 0,
  flashlightStudents: 0,
  flashlightCounted: 0,
  flashlightMatchedActive: 0,
  flashlightMatchedActiveOf: 0,
  flashlightMatchedActiveCounted: 0,
  flashlightMatched: 0,
  flashlightMatchedOf: 0,
  flashlightMatchedCounted: 0,
  errored: 0,
  unattempted: 0,
  ambiguous: 0,
  failed: 0,
  sources: {},
};

function strings(list: (string | null)[] | null | undefined): string[] {
  return (list ?? []).flatMap((item) => (item ? [item] : []));
}

function variants(list: (RawVariant | null)[] | null | undefined): AuditVariant[] {
  return (list ?? []).flatMap((raw) =>
    raw
      ? [{ traits: strings(raw.traits), students: raw.students ?? 0, sample: strings(raw.sample), ...(typeof raw.listed === 'number' ? { listed: raw.listed } : {}) }]
      : [],
  );
}

function variance(raw: RawVariance | null | undefined): AuditVariance | undefined {
  if (!raw) return undefined;
  const users = raw.users;
  return {
    students: raw.students ?? 0,
    plansGenerated: typeof raw.plansGenerated === 'boolean' ? raw.plansGenerated : undefined,
    onFlashlightRoster: typeof raw.onFlashlightRoster === 'number' ? raw.onFlashlightRoster : undefined,
    data: variants(raw.data),
    report: variants(raw.report),
    plan: variants(raw.plan),
    gaps: raw.gaps ?? {},
    users: users
      ? {
          admins: users.admins ?? 0,
          teachers: users.teachers ?? 0,
          checked: users.checked ?? 0,
          unanswered: users.unanswered ?? 0,
          empty: users.empty ?? 0,
          fewest: users.fewest ?? 0,
          median: users.median ?? 0,
          most: users.most ?? 0,
        }
      : undefined,
  };
}

const VERDICTS: AuditVerdict[] = ['ready', 'check', 'empty', 'unanswered'];

function verdictOf(raw: string | null | undefined): AuditVerdict {
  return VERDICTS.find((verdict) => verdict === raw) ?? 'unanswered';
}

function readiness(raw: RawReadiness | null | undefined): AuditTeacherReadiness | undefined {
  if (!raw) return undefined;
  const teachers = (raw.teachers ?? []).flatMap((t) => {
    if (!t || !t.sub) return [];
    const problems = (t.problems ?? []).flatMap((p) => (p && p.key ? [{ key: p.key, reasons: strings(p.reasons) }] : []));
    return [
      {
        sub: t.sub,
        name: t.name || t.sub,
        schools: strings(t.schools),
        students: t.students ?? 0,
        currentBenchmark: t.currentBenchmark ?? 0,
        currentWindow: t.currentWindow || undefined,
        flashlightStudents: typeof t.flashlightStudents === 'number' ? t.flashlightStudents : undefined,
        problemStudents: Math.max(t.problemStudents ?? 0, problems.length),
        problems,
        verdict: verdictOf(t.verdict),
        error: t.error || undefined,
      },
    ];
  });
  return {
    currentWindow: raw.currentWindow || undefined,
    teachers,
    ready: raw.ready ?? 0,
    check: raw.check ?? 0,
    empty: raw.empty ?? 0,
    unanswered: raw.unanswered ?? 0,
  };
}

function columns(raw: RawColumns | null | undefined): AuditRosterColumns | undefined {
  if (!raw) return undefined;
  return { rows: raw.rows ?? 0, named: strings(raw.named), missing: strings(raw.missing), filled: raw.filled ?? {} };
}

const COVERAGE_SOURCES: CoverageSource[] = ['feed', 'upload', 'wida', 'absent', 'empty'];

function coverage(raw: RawCoverage | null | undefined): AuditCoverage | undefined {
  if (!raw || !raw.fields) return undefined;
  const fields: Record<string, AuditFieldCoverage> = {};
  for (const [key, cell] of Object.entries(raw.fields)) {
    if (!cell) continue;
    fields[key] = { filled: cell.filled ?? 0, source: COVERAGE_SOURCES.find((source) => source === cell.source) ?? 'absent' };
  }
  return { fields };
}

function part(raw: RawPart | null | undefined): AuditPart | undefined {
  if (!raw) return undefined;
  return { of: raw.of ?? 1, landed: (raw.landed ?? []).filter((index): index is number => typeof index === 'number'), rosters: strings(raw.rosters), students: raw.students === true, graded: raw.graded === true };
}

function district(raw: RawDistrict): AuditDistrict {
  return {
    districtId: raw.districtId ?? '',
    districtName: raw.districtName ?? '',
    audited: raw.audited === true,
    auditedAt: raw.auditedAt || undefined,
    skippedAt: raw.skippedAt || undefined,
    uploads: raw.uploads ?? 0,
    students: raw.students ?? 0,
    fromUploads: typeof raw.fromUploads === 'number' ? raw.fromUploads : undefined,
    fromRosterStream: typeof raw.fromRosterStream === 'number' ? raw.fromRosterStream : undefined,
    fromFlashlight: typeof raw.fromFlashlight === 'number' ? raw.fromFlashlight : undefined,
    flashlightRefusal: raw.flashlightRefusal ? { rows: raw.flashlightRefusal.rows ?? 0, linked: raw.flashlightRefusal.linked ?? 0 } : undefined,
    attempted: raw.attempted ?? 0,
    resolved: raw.resolved ?? 0,
    notFound: raw.notFound ?? 0,
    foundByAltKey: raw.foundByAltKey ?? 0,
    flashlightStudents: typeof raw.flashlightStudents === 'number' ? raw.flashlightStudents : undefined,
    flashlightStudentsAll: typeof raw.flashlightStudentsAll === 'number' ? raw.flashlightStudentsAll : undefined,
    flashlightMatched: typeof raw.flashlightMatched === 'number' ? raw.flashlightMatched : undefined,
    flashlightMatchedActive: typeof raw.flashlightMatchedActive === 'number' ? raw.flashlightMatchedActive : undefined,
    flashlightMatchedFeed: typeof raw.flashlightMatchedFeed === 'number' ? raw.flashlightMatchedFeed : undefined,
    flashlightName: raw.flashlightName || undefined,
    feedBound: typeof raw.feedBound === 'boolean' ? raw.feedBound : undefined,
    trial: raw.trial === true ? true : undefined,
    state: raw.state || undefined,
    wida: typeof raw.wida === 'boolean' ? raw.wida : undefined,
    configured: typeof raw.configured === 'boolean' ? raw.configured : undefined,
    rosterStream: raw.rosterStream
      ? {
          present: raw.rosterStream.present === true,
          used: raw.rosterStream.used === true,
          rows: raw.rosterStream.rows ?? 0,
          refusal: raw.rosterStream.refusal || undefined,
          matched: raw.rosterStream.matched ?? 0,
          tested: raw.rosterStream.tested ?? 0,
          schoolYear: raw.rosterStream.schoolYear || undefined,
          empty: raw.rosterStream.empty === true,
          csvRosterUsed: raw.rosterStream.csvRosterUsed === true,
          csvRows: raw.rosterStream.csvRows ?? 0,
          csvLinked: raw.rosterStream.csvLinked ?? 0,
          rosterOfRecord: raw.rosterStream.rosterOfRecord === true,
          sharedColumns: sharedColumns(raw.rosterStream.sharedColumns),
          placeholderNames: placeholderNames(raw.rosterStream.placeholderNames),
          placeholderStudents: raw.rosterStream.placeholderStudents ?? 0,
        }
      : undefined,
    rosterColumns: columns(raw.rosterColumns),
    rosterStreamColumns: columns(raw.rosterStreamColumns),
    coverage: coverage(raw.coverage),
    lastUploadedAt: raw.lastUploadedAt || undefined,
    errored: raw.errored ?? 0,
    unattempted: raw.unattempted ?? 0,
    ambiguous: raw.ambiguous ?? 0,
    failed: raw.failed ?? 0,
    sources: raw.sources ?? {},
    batches: raw.batches ?? 0,
    failedBatches: raw.failedBatches ?? 0,
    error: raw.error || undefined,
    notFoundSample: strings(raw.notFoundSample),
    elapsedMs: raw.elapsedMs ?? 0,
    variance: variance(raw.variance),
    readiness: readiness(raw.readiness),
    part: part(raw.part),
  };
}

export async function fetchProgressReportAudit(): Promise<AuditReport> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/admin/progress-report-audit`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`admin-progress-report-audit fetch failed: ${response.status}`);
  }
  const data = ((await response.json()) as RawReport | null) ?? {};
  const parsed = (data.districts ?? []).flatMap((row) => (row ? [district(row)] : []));
  const configsUnknown = data.configsUnknown === true || parsed.some((row) => row.configured === undefined);
  const districts = configsUnknown ? parsed.map((row) => ({ ...row, state: undefined, wida: undefined, configured: undefined })) : parsed;
  return {
    computed: data.computed === true,
    kind: data.kind,
    runAt: data.runAt,
    trigger: data.trigger,
    incomplete: data.incomplete === true,
    usersUnknown: data.usersUnknown === true,
    configsUnknown,
    districts,
    totals: { ...EMPTY_TOTALS, ...(data.totals ?? {}), sources: data.totals?.sources ?? {} },
    elapsedMs: data.elapsedMs,
  };
}

export const FLASHLIGHT_ROSTER_SOURCE = 'flashlight360';

export const SOURCE_LABELS: Record<string, string> = {
  rosterstream: 'rosterstream',
  sis_upload: 'SIS upload',
  wida_upload: 'WIDA file only',
  [FLASHLIGHT_ROSTER_SOURCE]: 'Flashlight360 roster',
};

/** "rosterstream 812 · WIDA file only 1", largest first; empty when nothing is known. */
export function describeSources(sources: Record<string, number>): string {
  return Object.entries(sources)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([source, n]) => `${SOURCE_LABELS[source] ?? source} ${n}`)
    .join(' · ');
}

/** Students the audit reached a verdict on: looked up, or ambiguous and so never sent. */
export function checked(counts: { attempted: number; ambiguous: number }): number {
  return counts.attempted + counts.ambiguous;
}

export function describeKind(kind: string | undefined): string {
  if (!kind) return '';
  if (kind === 'report') return 'checking benchmarks and responses, as the report does';
  return `checking ${kind} only`;
}

/** Whole-percent share of `part` in `whole`, never above 100; null when there is nothing to divide by. */
export function share(part: number, whole: number): number | null {
  if (whole <= 0) return null;
  return Math.min(100, Math.round((part / whole) * 100));
}

export function formatRunAt(runAt: string): string {
  const date = new Date(runAt);
  if (Number.isNaN(date.getTime())) return runAt;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Denver',
    timeZoneName: 'short',
  });
}

export const GAP_LABELS: Record<string, string> = {
  benchmarks: 'no benchmark scores',
  access: 'no state test levels',
  rubric: 'newest benchmark has no rubric on record',
  response: 'a compared benchmark has no response',
  scores: 'a response has no scores',
  writing: 'a response has no writing',
  recording: 'a response has no playable recording',
  transcript: 'a recording has no transcript',
};

/** "no benchmark scores 12 · no state test levels 3", largest first. */
export function describeGaps(gaps: Record<string, number>): string {
  return Object.entries(gaps)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([kind, n]) => `${GAP_LABELS[kind] ?? kind} ${n}`)
    .join(' · ');
}

/** What the students a teacher can open get on their report; nothing when any group's openable count is unknown. */
export function describeOpenable(report: AuditVariant[]): string {
  if (report.length === 0 || report.some((variant) => variant.listed === undefined)) return '';
  const counts = { total: 0, blank: 0, accessOnly: 0, data: 0, unanswered: 0 };
  for (const variant of report) {
    const n = variant.listed ?? 0;
    const has = (trait: string) => variant.traits.includes(trait);
    counts.total += n;
    if (has('flashlight:unanswered')) counts.unanswered += n;
    else if (has('benchmarks:none') && has('writing:none') && has('speaking:none')) {
      if (has('access:none')) counts.blank += n;
      else counts.accessOnly += n;
    } else counts.data += n;
  }
  const line = `Of the ${counts.total} ${counts.total === 1 ? 'student' : 'students'} a teacher can open: ${counts.blank} get a blank report, ${counts.accessOnly} get ACCESS results only, ${counts.data} ${counts.data === 1 ? 'gets' : 'get'} Flashlight data`;
  return counts.unanswered > 0 ? `${line}, ${counts.unanswered} could not be looked up.` : `${line}.`;
}

/** What a teacher meets on arriving in Insights for this district, one sentence per fact the audit established. */
export function describeLanding(row: Pick<AuditDistrict, 'students' | 'flashlightStudents'>, v: AuditVariance): string[] {
  const lines: string[] = [];
  const users = v.users;
  if (!users) {
    lines.push('Who has Insights access in this district is not known.');
  } else if (users.admins + users.teachers === 0) {
    lines.push('Nobody in this district has Insights access yet.');
  } else {
    lines.push(`${count(users.admins, 'district admin')} and ${count(users.teachers, 'teacher')} have Insights access.`);
  }
  if (row.students === 0) {
    lines.push(
      'Insights has no roster for this district, only what Flashlight360 holds. A teacher lands on an empty roster that tells them to upload data, which teachers cannot do.',
    );
  } else if (v.onFlashlightRoster === undefined) {
    lines.push('Whether these students are on a Flashlight360 roster could not be checked, so what a teacher sees is not known.');
  } else {
    lines.push(
      `${v.onFlashlightRoster} of ${v.students} students are on a Flashlight360 roster under some id. A teacher sees only these, and only the ones in their own classes.`,
    );
  }
  if (users && users.checked > 0) {
    lines.push(
      `Opened the roster of ${count(users.checked, 'teacher')}: fewest ${users.fewest}, median ${users.median}, most ${users.most} students. ${users.empty} land on an empty roster.`,
    );
  }
  if (users && users.unanswered > 0) {
    lines.push(`${count(users.unanswered, 'teacher')} could not be checked.`);
  }
  if (users && users.teachers > users.checked + users.unanswered && users.checked + users.unanswered > 0) {
    lines.push(`The other ${users.teachers - users.checked - users.unanswered} teachers were not opened this run.`);
  }
  if (row.students > 0 && v.plansGenerated === false) {
    lines.push(
      'Action plans have not been generated this school year. Opening a student sends the teacher to the Plans page, which is not in their menu and which they cannot generate from.',
    );
  } else if (row.students > 0 && v.plansGenerated === undefined) {
    lines.push('Whether action plans have been generated could not be read.');
  }
  return lines;
}

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

export type Outcome = 'onRoster' | 'pastOnly' | 'found' | 'none' | 'unanswered';

export const OUTCOME_LABELS: Record<Outcome, string> = {
  onRoster: 'On FL360 roster',
  pastOnly: 'Past record only',
  found: 'Found in FL360',
  none: 'No FL360 record',
  unanswered: 'Not answered',
};

export type OutcomeCounts = Record<Outcome, number>;

/** Where a district's students stand with Flashlight 360; `found` holds every found student when their roster listing was not checked. */
export type MatchSplit = { students: number; rosterKnown: boolean; counts: OutcomeCounts };

export type GridRow = { label: string; counts: OutcomeCounts };

const NO_OUTCOMES: OutcomeCounts = { onRoster: 0, pastOnly: 0, found: 0, none: 0, unanswered: 0 };

const SOURCE_ROWS: { roster: string; wida: string; label: string }[] = [
  { roster: 'roster:rosterstream', wida: 'wida:record', label: 'RosterStream + WIDA' },
  { roster: 'roster:rosterstream', wida: 'wida:none', label: 'RosterStream only' },
  { roster: 'roster:sis_upload', wida: 'wida:record', label: 'SIS upload + WIDA' },
  { roster: 'roster:sis_upload', wida: 'wida:none', label: 'SIS upload only' },
  { roster: 'roster:none', wida: 'wida:record', label: 'WIDA file only' },
];

function outcomeOf(traits: string[]): Outcome {
  if (traits.includes('flashlight:not_found')) return 'none';
  if (!traits.includes('flashlight:found')) return 'unanswered';
  if (traits.includes('flashlight_roster:listed')) return 'onRoster';
  if (traits.includes('flashlight_roster:absent')) return 'pastOnly';
  return 'found';
}

function foldFound(counts: OutcomeCounts): OutcomeCounts {
  return { ...counts, onRoster: 0, pastOnly: 0, found: counts.onRoster + counts.pastOnly + counts.found };
}

/** The split over every student the district checked: students the tally stopped short of, or never sent, read as not answered. */
export function splitMatches(v: AuditVariance | undefined, total = v?.students ?? 0): MatchSplit | null {
  if (!v || (v.students === 0 && total === 0)) return null;
  const students = Math.max(v.students, total);
  const counts = { ...NO_OUTCOMES };
  for (const variant of v.data) counts[outcomeOf(variant.traits)] += variant.students;
  const tallied = Object.values(counts).reduce((sum, n) => sum + n, 0);
  if (tallied < students) counts.unanswered += students - tallied;
  const rosterKnown = counts.found === 0;
  return { students, rosterKnown, counts: rosterKnown ? counts : foldFound(counts) };
}

export function splitColumns(split: MatchSplit): Outcome[] {
  const columns: Outcome[] = split.rosterKnown ? ['onRoster', 'pastOnly', 'none'] : ['found', 'none'];
  return split.counts.unanswered > 0 ? [...columns, 'unanswered'] : columns;
}

export function sourceGrid(v: AuditVariance, total = v.students): GridRow[] {
  const split = splitMatches(v, total);
  const rosterKnown = split?.rosterKnown ?? true;
  const rows = SOURCE_ROWS.map((source) => ({ ...source, counts: { ...NO_OUTCOMES } }));
  const other = { label: 'Other', counts: { ...NO_OUTCOMES } };
  const untallied = { label: 'Not tallied', counts: { ...NO_OUTCOMES } };
  let tallied = 0;
  for (const variant of v.data) {
    const row = rows.find((candidate) => variant.traits.includes(candidate.roster) && variant.traits.includes(candidate.wida)) ?? other;
    row.counts[outcomeOf(variant.traits)] += variant.students;
    tallied += variant.students;
  }
  if (split && tallied < split.students) untallied.counts.unanswered = split.students - tallied;
  return [...rows, other, untallied]
    .filter((row) => Object.values(row.counts).some((n) => n > 0))
    .map((row) => ({ label: row.label, counts: rosterKnown ? row.counts : foldFound(row.counts) }));
}

type SplitRow = Pick<AuditDistrict, 'audited' | 'students' | 'attempted' | 'ambiguous' | 'variance'>;

function hasData(row: Pick<AuditDistrict, 'audited' | 'students'>): boolean {
  return row.audited && row.students > 0;
}

/** The split summed over every audited district with data; null unless each of them could be split. */
export function splitTotals(districts: SplitRow[]): MatchSplit | null {
  const total: MatchSplit = { students: 0, rosterKnown: true, counts: { ...NO_OUTCOMES } };
  let summed = 0;
  for (const row of districts) {
    if (!hasData(row)) continue;
    const split = splitMatches(row.variance, checked(row));
    if (!split) return null;
    summed++;
    total.students += split.students;
    total.rosterKnown = total.rosterKnown && split.rosterKnown;
    for (const outcome of Object.keys(total.counts) as Outcome[]) total.counts[outcome] += split.counts[outcome];
  }
  if (summed === 0) return null;
  return total.rosterKnown ? total : { ...total, counts: foldFound(total.counts) };
}

export type SideRate = { matched: number; of: number; districts: number; skipped: number };

type SideRow = Pick<AuditDistrict, 'audited' | 'students'> & Partial<Pick<AuditDistrict, 'flashlightStudents' | 'flashlightStudentsAll' | 'flashlightMatched' | 'flashlightMatchedActive'>>;

function sideCounts(row: SideRow, which: 'active' | 'all'): { matched: number; of: number } | null {
  const matched = which === 'active' ? row.flashlightMatchedActive : row.flashlightMatched;
  const of = which === 'active' ? row.flashlightStudents : row.flashlightStudentsAll;
  return matched === undefined || of === undefined ? null : { matched, of };
}

/** How many of the Flashlight 360 students the district data reached, over the audited districts that have data; `bothOnly` keeps to the districts that carry the active and the all counts alike, so the two rates cover one cohort. */
export function flashlightSide(districts: SideRow[], which: 'active' | 'all', bothOnly = false): SideRate {
  const rate: SideRate = { matched: 0, of: 0, districts: 0, skipped: 0 };
  for (const row of districts) {
    if (!hasData(row)) continue;
    const counts = sideCounts(row, which);
    if (!counts || (bothOnly && !sideCounts(row, which === 'active' ? 'all' : 'active'))) {
      rate.skipped++;
      continue;
    }
    rate.districts++;
    rate.matched += counts.matched;
    rate.of += counts.of;
  }
  return rate;
}

type NameRow = Pick<AuditDistrict, 'districtName' | 'audited' | 'students'> & Partial<Pick<AuditDistrict, 'flashlightStudents'>>;

/** The audited districts Insights has no students for, the ones with the most Flashlight 360 students first. */
export function withoutData(districts: NameRow[]): { name: string; flashlightStudents: number | undefined }[] {
  return districts
    .filter((row) => row.audited && !hasData(row))
    .map((row) => ({ name: row.districtName, flashlightStudents: row.flashlightStudents }))
    .sort((a, b) => (b.flashlightStudents ?? -1) - (a.flashlightStudents ?? -1));
}

type FeedRow = Pick<AuditDistrict, 'districtName' | 'audited' | 'students'> & Partial<Pick<AuditDistrict, 'flashlightStudents' | 'flashlightMatchedFeed' | 'flashlightName' | 'feedBound' | 'trial'>> & { rosterStream?: Pick<AuditFeedVerdict, 'present'> };

export type FeedRate = SideRate & { trials: number; fed: number };

export function isTrial(row: Pick<FeedRow, 'trial'>): boolean {
  return row.trial === true;
}

export function hasFeed(row: Pick<FeedRow, 'feedBound' | 'rosterStream'>): boolean {
  return row.feedBound ?? row.rosterStream?.present ?? false;
}

function byFlashlightStudents(a: Pick<FeedRow, 'flashlightStudents'>, b: Pick<FeedRow, 'flashlightStudents'>): number {
  return (b.flashlightStudents ?? -1) - (a.flashlightStudents ?? -1);
}

export function rosterStreamSide(districts: FeedRow[]): FeedRate {
  const rate: FeedRate = { matched: 0, of: 0, districts: 0, skipped: 0, trials: 0, fed: 0 };
  for (const row of districts) {
    if (isTrial(row)) {
      rate.trials++;
      continue;
    }
    if (!hasFeed(row)) continue;
    rate.fed++;
    if (row.flashlightMatchedFeed === undefined || row.flashlightStudents === undefined) {
      rate.skipped++;
      continue;
    }
    rate.districts++;
    rate.matched += row.flashlightMatchedFeed;
    rate.of += row.flashlightStudents;
  }
  return rate;
}

export function withoutFeed<T extends FeedRow>(districts: T[]): { rows: T[]; insight: T[] } {
  const insight = districts.filter((row) => !isTrial(row));
  const rows = insight.filter((row) => !hasFeed(row)).sort(byFlashlightStudents);
  return { rows, insight };
}

export function trialNames(districts: FeedRow[]): string[] {
  return districts
    .filter(isTrial)
    .sort(byFlashlightStudents)
    .map((row) => row.flashlightName || row.districtName);
}

type VerdictRow = Pick<AuditDistrict, 'audited' | 'attempted' | 'ambiguous' | 'resolved' | 'students'> &
  Partial<Pick<AuditDistrict, 'flashlightStudents' | 'flashlightStudentsAll' | 'flashlightMatched' | 'flashlightMatchedActive' | 'variance' | 'configured' | 'uploads' | 'sources' | 'rosterStream' | 'errored' | 'unattempted' | 'error'>>;

/** Every audited student came from Flashlight 360's own student list: the district uploaded nothing and bound no feed. */
export function rosteredFromFlashlight(row: Pick<VerdictRow, 'students' | 'sources'>): boolean {
  const fromFlashlight = row.sources?.[FLASHLIGHT_ROSTER_SOURCE] ?? 0;
  return row.students > 0 && fromFlashlight === row.students;
}

/** "nothing is uploaded or fed to Insights", or the inputs the district does have that produced no roster student. */
function noRosterInputs(row: Pick<VerdictRow, 'uploads' | 'rosterStream'>): string {
  const inputs: string[] = [];
  if (row.uploads) inputs.push(`${row.uploads} ${row.uploads === 1 ? 'upload' : 'uploads'}`);
  if (row.rosterStream?.present) inputs.push('bound feed');
  if (inputs.length === 0) return 'nothing is uploaded or fed to Insights';
  return `the district's ${inputs.join(' and its ')} yielded no roster students`;
}

/** The audit Lambda's own note on work its deadline reserve refused (progressreportaudit.CutOff). */
const CUT_OFF_PREFIX = 'not audited: the run reached its time limit';
/** Every other note the audit itself leaves on a row (a shard still out, or one that could not start) starts like this; any other error on the row is a resolver batch failing. */
const RUN_NOTE_PREFIX = 'not audited';

function unattemptedNote(unattempted: number, error: string | undefined): string {
  if (!error || error.startsWith(CUT_OFF_PREFIX)) return `${unattempted} were not attempted before the audit's deadline`;
  if (error.startsWith(RUN_NOTE_PREFIX)) return `${unattempted} were not attempted before the last audit finished (${error})`;
  return `${unattempted} were not attempted because a resolver batch failed (${error})`;
}

/** Students Flashlight 360 lists that the roster could not key (no student id or number the resolver matches, or no user id); unknown when it did not report its count. */
function omittedFromFlashlight(row: Pick<VerdictRow, 'students' | 'flashlightStudents'>): number | undefined {
  return row.flashlightStudents === undefined ? undefined : Math.max(0, row.flashlightStudents - row.students);
}

function describeFlashlightRoster(row: VerdictRow): string {
  const omitted = omittedFromFlashlight(row);
  const keyed = omitted !== undefined && omitted > 0 ? ' that Insights could key' : '';
  const runs =
    row.configured === true
      ? ' and reports run on its data alone'
      : row.configured === false
        ? ", but reports need the district's resolver plans before they can run on its data"
        : '; whether reports run on its data depends on resolver plans this run could not list';
  const lead = `Roster from Flashlight360: ${noRosterInputs(row)}, so the ${row.students} students Flashlight360 lists here${keyed} are the roster${runs}.`;
  const gap =
    omitted === undefined
      ? ' Flashlight360 did not report its student count, so how many more it lists that Insights could not key is not known.'
      : omitted > 0
        ? ` ${omitted} more it lists could not be keyed (no student id or number the resolver matches, or no user id) and cannot be offered a report.`
        : '';
  const errored = row.errored ?? 0;
  const unattempted = row.unattempted ?? 0;
  const outages: string[] = [];
  if (errored > 0) outages.push(`${errored} hit a resolver error`);
  if (unattempted > 0) outages.push(unattemptedNote(unattempted, row.error));
  const total = checked(row);
  const sampled = Math.max(0, row.students - total);
  const unchecked = sampled > 0 ? `${sampled} were not checked in this sampled run` : '';
  if (total === 0) return `${lead}${gap} None of them could be checked.`;
  if (row.resolved >= total && outages.length === 0) {
    return sampled > 0 ? `${lead}${gap} All ${total} checked resolved; ${unchecked}.` : `${lead}${gap} Every one of them resolved.`;
  }
  const unmatched = total - row.resolved - errored - unattempted;
  const reasons = [sampled > 0 ? `${row.resolved} of ${total} checked resolved` : `${row.resolved} of ${row.students} resolved`, ...outages];
  if (unmatched > 0) reasons.push(`${outages.length === 0 ? 'the rest' : unmatched} have an id the resolver cannot match or a plan that does not cover them`);
  if (unchecked) reasons.push(unchecked);
  return `${lead}${gap} ${reasons.join('; ')}.`;
}

export type DistrictTier = 'data' | 'usersOnly' | 'configuredOnly' | 'bare';

type TierRow = Pick<AuditDistrict, 'audited' | 'students'> & Partial<Pick<AuditDistrict, 'configured' | 'variance'>>;

function hasUsers(row: Pick<TierRow, 'variance'>): boolean {
  const users = row.variance?.users;
  return users !== undefined && users.admins + users.teachers > 0;
}

/** Where an audited district stands: data in Insights, people but no data, plans for the resolver only, or none of those known. */
export function tierOf(row: TierRow): DistrictTier {
  if (row.students > 0) return 'data';
  if (hasUsers(row)) return 'usersOnly';
  if (row.configured === true && row.variance?.users !== undefined) return 'configuredOnly';
  return 'bare';
}

export function tiers<T extends TierRow>(districts: T[]): Record<DistrictTier, T[]> {
  const out: Record<DistrictTier, T[]> = { data: [], usersOnly: [], configuredOnly: [], bare: [] };
  for (const row of districts) {
    if (row.audited) out[tierOf(row)].push(row);
  }
  return out;
}

/** "WIDA states: 20 districts · not WIDA: 1 (Tex City, Texas) · state unknown: 2" over every listed district; nothing when the configs could not be listed, since no state is then known. */
export function describeWida(districts: (Pick<AuditDistrict, 'districtName'> & Partial<Pick<AuditDistrict, 'state' | 'wida'>>)[], configsKnown: boolean): string {
  if (!configsKnown || districts.length === 0) return '';
  const wida = districts.filter((row) => row.wida === true).length;
  const outside = districts.filter((row) => row.wida === false);
  const unknown = districts.filter((row) => row.wida === undefined).length;
  const parts = [`WIDA states: ${wida} ${wida === 1 ? 'district' : 'districts'}`];
  if (outside.length > 0) parts.push(`not WIDA: ${outside.length} (${outside.map((row) => `${row.districtName}, ${row.state}`).join('; ')})`);
  if (unknown > 0) parts.push(`state unknown: ${unknown}`);
  return parts.join(' · ');
}

const highRate = 0.75;
const halfRate = 0.5;

/** One sentence on what the two match rates say about the district, for a reader who has not studied the counts. */
export function describeVerdict(row: VerdictRow): string {
  if (!row.audited) return '';
  if (rosteredFromFlashlight(row)) return describeFlashlightRoster(row);
  const total = checked(row);
  if (!hasData(row)) {
    if (row.configured === true && row.variance?.users !== undefined && !hasUsers(row)) {
      const lists = row.flashlightStudents === undefined ? '' : `, which lists ${row.flashlightStudents} students here`;
      const uploads = row.uploads ? `its ${row.uploads} ${row.uploads === 1 ? 'upload holds' : 'uploads hold'} no students` : 'nothing has been uploaded';
      return `Configured for Flashlight360${lists}; nobody has Insights access yet and ${uploads}.`;
    }
    return row.flashlightStudents === undefined
      ? 'No data in Insights.'
      : `No data in Insights. Flashlight360 lists ${row.flashlightStudents} students here with nothing to match them against.`;
  }
  const districtPct = share(row.resolved, total) ?? 0;
  const districtSide = `${districtPct}% of district students have an FL360 record`;
  const active = sideCounts(row, 'active');
  const side = active ?? sideCounts(row, 'all');
  const listed = row.flashlightStudents ?? row.flashlightStudentsAll;
  if (total === 0) {
    const reverse = side ? ` ${side.matched} of ${side.of} ${active ? 'active FL360 students' : 'FL360 students, current or dropped,'} are in the data.` : '';
    return `None of the ${row.students} students in the district's data could be checked, so the district side cannot be judged.${reverse}`;
  }
  if (!side) {
    if (listed === undefined) return `Flashlight360 did not report its student count, so only the district side can be judged: ${districtSide}.`;
    return `Flashlight360 lists ${listed} students here, but how many of them the data reached is not known: a batch was not attempted. ${districtSide}.`;
  }
  if (side.of === 0) {
    if (row.resolved === 0) return `Flashlight360 lists no students for this district, and no lookup found any of the ${total} students in its data: nothing matched on either side.`;
    return `Flashlight360 lists no students for this district, yet ${districtSide.replace('have an FL360 record', 'have a record there')}: the records are in another district or a past year.`;
  }
  const noun = active ? 'active FL360 students' : 'FL360 students, current or dropped,';
  const singular = active ? 'active FL360 student' : 'FL360 student, current or dropped,';
  const flashlightPct = share(side.matched, side.of) ?? 0;
  const flashlightHigh = side.matched >= side.of * highRate;
  const districtHigh = row.resolved >= total * highRate;
  if (flashlightHigh && districtHigh) {
    return `The district's data and Flashlight360 list the same students: ${flashlightPct}% of ${noun} are in the data and ${districtPct}% of district students have an FL360 record.`;
  }
  if (flashlightHigh) {
    const split = splitMatches(row.variance, total);
    const known = split !== null && split.rosterKnown;
    const unanswered = known && split.counts.unanswered > 0 ? `, and ${split.counts.unanswered} could not be looked up` : '';
    const have = known ? `are on an FL360 roster today. The rest have a past record or none${unanswered}.` : `have an FL360 record the audit could find.`;
    const n = known ? split.counts.onRoster : row.resolved;
    return `Nearly every ${singular} is in the district's data (${flashlightPct}%), but only ${n} of the ${total} students the district reports ${have}`;
  }
  if (row.resolved >= total * halfRate) {
    return `The district's data covers only part of Flashlight360: ${side.of - side.matched} of ${side.of} ${noun} are not in it, while ${districtPct}% of the students it does list have an FL360 record.`;
  }
  return `Few students match in either direction: ${districtPct}% of district students have an FL360 record and ${flashlightPct}% of ${noun} are in the district's data. The ids on each side do not line up; check which id the upload carries.`;
}

/** One sentence on a bound RosterStream feed; nothing when none is bound. */
export function describeFlashlightRefusal(r: AuditFlashlightRefusal | undefined): string | null {
  if (!r) return null;
  return `Only ${r.linked} of ${r.rows} roster students were found on the Flashlight360 roster, so the students it lists and the district data does not are not shown.`;
}

export function describeFeed(v: AuditFeedVerdict | undefined): string {
  if (!v || !v.present) return '';
  const fallback = v.csvRosterUsed ? 'the uploaded roster stood in' : 'no uploaded roster stood in, so the report runs on the ACCESS file alone';
  if (v.empty) return `RosterStream feed: bound but it has no students yet; ${fallback}.`;
  const match =
    v.tested === 0
      ? 'there was no WIDA cohort in the report year to match it against'
      : `${v.matched} of ${v.tested} WIDA students ${v.schoolYear ? `in ${v.schoolYear}` : 'in the report year'} matched it`;
  const link = (v.csvRows ?? 0) > 0 ? `${v.csvLinked ?? 0} of ${v.csvRows} uploaded roster students were found on it` : '';
  if (v.used) {
    const role = (v.csvRows ?? 0) === 0 ? '' : v.rosterOfRecord ? ', and it is the roster of record' : ', so it enriches the upload rather than replacing it';
    return `RosterStream feed: in use, ${v.rows} students on it, ${match}${link ? `, ${link}${role}` : ''}.`;
  }
  // A feed the WIDA cohort matched can still be refused for the upload it could
  // not be folded onto; that shortfall, not the match it passed, is the reason.
  const reason = v.refusal?.includes('uploaded roster') && link ? link : match;
  return `RosterStream feed: bound, ${v.rows} students on it, refused because ${reason}; ${fallback}.`;
}

export const ROSTER_FIELD_LABELS: Record<string, string> = {
  student_id: 'student id',
  state_student_id: 'state student id',
  school: 'school',
  grade: 'grade',
  el_status: 'EL status',
  el_service_start: 'EL service start',
  exit_date: 'exit date',
  exit_reason: 'exit reason',
  program_model: 'program model',
  home_language: 'home language',
  dually_identified: 'dually identified',
  alternate_access: 'alternate access',
  newcomer_flag: 'newcomer flag',
  assigned_teacher: 'assigned teacher',
  originating_district: 'originating district',
  class: 'class or homeroom',
};

/** The roster columns the uploads left out, and the named ones fewer than half the rows fill; empty when the run did not look. */
export function describeColumns(c: AuditRosterColumns | undefined): string[] {
  if (!c) return [];
  const label = (field: string) => ROSTER_FIELD_LABELS[field] ?? field;
  const lines: string[] = [];
  if (c.missing.length > 0) lines.push(`Roster columns missing from the uploads: ${c.missing.map(label).join(', ')}.`);
  else lines.push('Every roster column the audit looks for is named in the uploads.');
  const thin = c.named.filter((field) => (c.filled[field] ?? 0) * 2 < c.rows);
  if (thin.length > 0) lines.push(`Named but mostly blank: ${thin.map((field) => `${label(field)} (${c.filled[field] ?? 0} of ${c.rows} rows)`).join(', ')}.`);
  return lines;
}

export type CoverageGroup = 'every' | 'some';

export type CoverageField = { key: string; label: string; short: string; group: CoverageGroup };

export const COVERAGE_FIELDS: CoverageField[] = [
  { key: 'student_id', label: 'Student ID', short: 'Student ID', group: 'every' },
  { key: 'school', label: 'School', short: 'School', group: 'every' },
  { key: 'grade', label: 'Grade level', short: 'Grade', group: 'every' },
  { key: 'el_status', label: 'EL status', short: 'EL status', group: 'every' },
  { key: 'el_service_start', label: 'EL service start date', short: 'EL start date', group: 'every' },
  { key: 'program_model', label: 'Program model', short: 'Program model', group: 'every' },
  { key: 'home_language', label: 'Home language', short: 'Home language', group: 'every' },
  { key: 'assigned_teacher', label: 'Assigned EL teacher', short: 'EL teacher', group: 'every' },
  { key: 'exit_date', label: 'Exit / reclassification date', short: 'Exit date', group: 'some' },
  { key: 'exit_reason', label: 'Exit reason', short: 'Exit reason', group: 'some' },
  { key: 'dually_identified', label: 'Dually identified (IEP / 504)', short: 'Dually ident.', group: 'some' },
  { key: 'alternate_access', label: 'Alternate ACCESS flag', short: 'Alt ACCESS', group: 'some' },
  { key: 'newcomer_flag', label: 'Newcomer flag', short: 'Newcomer', group: 'some' },
];

export const COVERAGE_THIN = 80;

export type CoverageTint = 'red' | 'yellow' | 'ok';

export const COVERAGE_SOURCE_LABELS: Record<CoverageSource, string> = {
  feed: 'RosterStream',
  upload: 'SIS upload',
  wida: 'WIDA file',
  absent: 'Not in feed or upload',
  empty: 'Column present, no values',
};

const NO_VALUE: AuditFieldCoverage = { filled: 0, source: 'absent' };

export function coverageCell(coverage: AuditCoverage | undefined, key: string): AuditFieldCoverage {
  return coverage?.fields[key] ?? NO_VALUE;
}

function missing(cell: AuditFieldCoverage): boolean {
  return cell.source === 'absent' || cell.source === 'empty';
}

export function coverageTint(field: CoverageField, cell: AuditFieldCoverage, students: number): CoverageTint {
  if (field.group === 'every') {
    if (missing(cell)) return 'red';
    return students === 0 || cell.filled * 100 < students * COVERAGE_THIN ? 'yellow' : 'ok';
  }
  return missing(cell) ? 'yellow' : 'ok';
}

export function coverageCellText(cell: AuditFieldCoverage, students: number): string {
  const pct = missing(cell) ? null : share(cell.filled, students);
  return pct === null ? 'none' : `${pct}%`;
}

export type CoverageGrade = { red: number; yellow: number; rank: 0 | 1 | 2; problems: number };

type CoverageRankRow = Pick<AuditDistrict, 'districtId' | 'districtName' | 'students'> & { coverage?: AuditCoverage; rosterStream?: Pick<AuditFeedVerdict, 'sharedColumns' | 'placeholderNames'> };

export function gradeCoverage(row: Pick<CoverageRankRow, 'students' | 'coverage' | 'rosterStream'>): CoverageGrade {
  let red = (row.rosterStream?.sharedColumns?.length ?? 0) + ((row.rosterStream?.placeholderNames?.length ?? 0) > 0 ? 1 : 0);
  let yellow = 0;
  for (const field of COVERAGE_FIELDS) {
    const tint = coverageTint(field, coverageCell(row.coverage, field.key), row.students);
    if (tint === 'red') red++;
    if (tint === 'yellow') yellow++;
  }
  return { red, yellow, rank: red > 0 ? 2 : yellow > 0 ? 1 : 0, problems: red + yellow };
}

export type CoverageRow<T extends CoverageRankRow> = { row: T; grade: CoverageGrade };

export function rankCoverage<T extends CoverageRankRow>(districts: T[]): CoverageRow<T>[] {
  return districts
    .filter((row) => row.coverage !== undefined)
    .map((row) => ({ row, grade: gradeCoverage(row) }))
    .sort((a, b) => b.grade.rank - a.grade.rank || b.grade.problems - a.grade.problems || a.row.districtName.localeCompare(b.row.districtName) || a.row.districtId.localeCompare(b.row.districtId));
}

export type CoverageFilter = 'all' | 'problems' | 'red' | 'clean';

export const COVERAGE_FILTERS: { key: CoverageFilter; label: string }[] = [
  { key: 'all', label: 'All districts' },
  { key: 'problems', label: 'With a problem' },
  { key: 'red', label: 'Missing a required field' },
  { key: 'clean', label: 'Complete' },
];

export function filterCoverage<T extends CoverageRankRow>(rows: CoverageRow<T>[], query: string, filter: CoverageFilter): CoverageRow<T>[] {
  const needle = query.trim().toLowerCase();
  return rows.filter(({ row, grade }) => {
    if (needle && !(row.districtName || row.districtId).toLowerCase().includes(needle)) return false;
    if (filter === 'problems') return grade.rank > 0;
    if (filter === 'red') return grade.rank === 2;
    if (filter === 'clean') return grade.rank === 0;
    return true;
  });
}

export function coverageSummary<T extends CoverageRankRow>(rows: CoverageRow<T>[]): { districts: number; red: number; yellow: number; clean: number } {
  const red = rows.filter((entry) => entry.grade.rank === 2).length;
  const yellow = rows.filter((entry) => entry.grade.rank === 1).length;
  return { districts: rows.length, red, yellow, clean: rows.length - red - yellow };
}

const ROSTER_SOURCES: CoverageSource[] = ['feed', 'upload', 'wida'];

function sharedColumns(raw: unknown): AuditSharedColumn[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = raw
    .filter((c): c is AuditSharedColumn => typeof c?.column === 'string' && Array.isArray(c.fields) && Array.isArray(c.labels))
    .map((c) => ({ column: c.column, fields: c.fields.map(String), labels: c.labels.map(String) }));
  return out.length > 0 ? out : undefined;
}

function placeholderNames(raw: unknown): AuditPlaceholderName[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = raw
    .filter((n): n is AuditPlaceholderName => typeof n?.value === 'string' && n.value !== '' && typeof n.students === 'number')
    .map((n) => ({ value: n.value, students: n.students }));
  return out.length > 0 ? out : undefined;
}

export function describePlaceholderNames(names: AuditPlaceholderName[] | undefined, students: number): string[] {
  const listed = names ?? [];
  const lines = listed.map((n) => `“${n.value}” stands in for the ${n.students === 1 ? 'name of 1 student' : `names of ${n.students} students`} in the feed.`);
  const shown = listed.reduce((sum, n) => sum + n.students, 0);
  if (lines.length > 0 && students > shown) lines.push(`${students} students in all carry a placeholder name; only the most common values are listed.`);
  return lines;
}

export function placeholderNamesLine(names: AuditPlaceholderName[] | undefined, students: number, feedUsed: boolean): string {
  const lines = describePlaceholderNames(names, students);
  if (lines.length === 0) return '';
  const closing = feedUsed
    ? 'Insight leaves those placeholders out: a student with an ACCESS record keeps the name from ACCESS, the rest show no name until the district sends real names in its SIS feed.'
    : 'The feed is not in use (its refusal is above), so Insight shows names from the uploaded roster and ACCESS files; once the feed is in use, its placeholders are left out the same way.';
  return `${lines.join(' ')} ${closing}`;
}

function listNames(names: string[]): string {
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function describeSharedColumns(shared: AuditSharedColumn[] | undefined): string[] {
  return (shared ?? []).map((c) => `${listNames(c.labels)} ${c.labels.length > 2 ? 'all' : 'both'} read the feed column “${c.column}”.`);
}

export function describeCoverageSources(coverage: AuditCoverage | undefined): string {
  const present = ROSTER_SOURCES.filter((source) => COVERAGE_FIELDS.some((field) => coverageCell(coverage, field.key).source === source));
  if (present.length === 0) return 'No roster source';
  if (present.length === 1) return `${COVERAGE_SOURCE_LABELS[present[0] as CoverageSource]} only`;
  return present.map((source) => COVERAGE_SOURCE_LABELS[source]).join(' + ');
}

function formatUploadDate(at: string): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return at;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Denver' });
}

function describeFeedState(v: AuditFeedVerdict | undefined): string {
  if (!v || !v.present) return 'No RosterStream feed';
  if (v.empty || v.rows === 0) return 'RosterStream feed bound, no students yet';
  return v.used ? 'RosterStream feed in use' : 'RosterStream feed bound, refused';
}

function describeStateLine(row: Partial<Pick<AuditDistrict, 'state' | 'wida'>>): string {
  if (row.wida === undefined) return row.state ? `State: ${row.state}` : 'State unknown';
  return row.wida ? `WIDA state (${row.state})` : `Not a WIDA state (${row.state})`;
}

type CoverageHeaderRow = Pick<AuditDistrict, 'uploads'> & Partial<Pick<AuditDistrict, 'rosterStream' | 'lastUploadedAt' | 'state' | 'wida' | 'auditedAt'>>;

export function describeCoverageHeader(row: CoverageHeaderRow): string {
  const parts = [describeFeedState(row.rosterStream), row.uploads > 0 && row.lastUploadedAt ? `Latest upload ${formatUploadDate(row.lastUploadedAt)}` : 'No uploads', describeStateLine(row)];
  if (row.auditedAt) parts.push(`audited ${formatRunAt(row.auditedAt)}`);
  return parts.join(' · ');
}

export type VariantGroup = 'report' | 'plan';

export const VARIANT_COLUMNS: Record<VariantGroup, string[]> = {
  report: ['Benchmarks', 'State test', 'Writing', 'Speaking'],
  plan: ['Scored years', 'SIS fields', 'FL benchmarks', 'FL assignments'],
};

function pick(traits: string[], prefix: string, labels: Record<string, string>, fallback: string): string {
  const code = traits.find((trait) => trait.startsWith(`${prefix}:`))?.slice(prefix.length + 1);
  return code === undefined ? fallback : (labels[code] ?? code);
}

/** One short status per column of VARIANT_COLUMNS for a report or plan variant. */
export function describeCells(group: VariantGroup, traits: string[]): string[] {
  const has = (trait: string) => traits.includes(trait);
  if (group === 'report') {
    if (has('flashlight:unanswered')) return ['not answered', 'not answered', 'not answered', 'not answered'];
    const benchmarks = [pick(traits, 'benchmarks', { none: 'none', one: 'one', growth: 'two or more', past_years: '' }, 'none')];
    if (has('benchmarks:past_years')) benchmarks.push('past year');
    if (has('rubric:changed')) benchmarks.push('earlier rubric');
    if (has('rubric:unknown')) benchmarks.push('rubric unknown');
    const access = pick(traits, 'access', { none: 'none', latest: 'latest', prior: 'latest + prior', alternate: '' }, 'none');
    return [
      benchmarks.join(', '),
      has('access:alternate') ? `${access} (Alternate)` : access,
      pick(traits, 'writing', { none: 'none', shown: 'sample' }, 'none'),
      pick(traits, 'speaking', { none: 'none', recording: 'recording', transcript: 'transcript' }, 'none'),
    ];
  }
  if (has('plan:none')) return ['no plan: opens the profile', '—', '—', '—'];
  const scores = pick(traits, 'scores', { none: 'none', one_year: 'one', trajectory: 'two or more', alternate: '' }, 'none');
  const shown = { shown: 'yes', none: 'none', unanswered: 'not answered' };
  return [
    has('scores:alternate') ? `${scores} (Alternate)` : scores,
    pick(traits, 'sis', { roster: 'yes', none: 'none' }, 'none'),
    pick(traits, 'plan_benchmarks', shown, 'none'),
    pick(traits, 'plan_assignments', shown, 'none'),
  ];
}

export const VERDICT_LABELS: Record<AuditVerdict, string> = {
  ready: 'Ready',
  check: 'Check',
  empty: 'Empty',
  unanswered: 'Unanswered',
};

export const VERDICT_MEANINGS: Record<AuditVerdict, string> = {
  ready: 'students listed, at least one with a current benchmark, no problem students',
  check: 'students listed but none with a current benchmark, or some reports to check',
  empty: 'the teacher would see no students',
  unanswered: 'the roster could not be opened this run',
};

export const PROBLEM_LABELS: Record<string, string> = {
  response: GAP_LABELS.response ?? 'response',
  scores: GAP_LABELS.scores ?? 'scores',
  writing: GAP_LABELS.writing ?? 'writing',
  recording: GAP_LABELS.recording ?? 'recording',
  transcript: GAP_LABELS.transcript ?? 'transcript',
  rubric: GAP_LABELS.rubric ?? 'rubric',
  access: 'ACCESS record uploaded, none shown on the report',
  rubric_changed: 'rubric changed between the shown windows',
  rubric_unknown: 'rubric could not be read',
  ambiguous_id: 'id shared with another student',
  alternate_id: 'found only by another id',
  unanswered: 'Flashlight360 did not answer',
  unchecked: 'not looked up this run',
  unbound_identity: 'archive data that could belong to a former student with the same id',
};

/** "168266: a recording has no transcript · id shared with another student". */
export function describeProblem(problem: AuditStudentProblem): string {
  return `${problem.key}: ${problem.reasons.map((reason) => PROBLEM_LABELS[reason] ?? reason).join(' · ')}`;
}

/** Why a teacher got their verdict, in terms of what their Progress Reports table would show; nothing for a ready teacher. */
export function verdictNote(row: AuditTeacherResult, districtWindow: string | undefined): string {
  const window = row.currentWindow ?? districtWindow;
  switch (row.verdict) {
    case 'unanswered':
      return row.error || 'could not be checked';
    case 'empty':
      if (row.problemStudents > 0) {
        return `${row.problemStudents} roster ${row.problemStudents === 1 ? 'row shares' : 'rows share'} an id with another student, so the table cannot offer them`;
      }
      if (row.flashlightStudents === undefined) return 'no students; the FL360 count could not be read';
      if (row.flashlightStudents === 0) return 'no FL360 students in their classes';
      return `FL360 lists ${row.flashlightStudents} students in their classes, none on the uploaded roster`;
    case 'check':
      if (row.currentBenchmark === 0) {
        const lists = row.flashlightStudents === undefined ? '' : `; FL360 lists ${row.flashlightStudents}`;
        return `no ${window ?? 'current'} benchmark yet${lists}`;
      }
      return `${row.problemStudents} ${row.problemStudents === 1 ? 'report' : 'reports'} to check`;
    default:
      return '';
  }
}

/** A cell a spreadsheet would read as a formula is prefixed with an apostrophe, so it opens as text. */
function csvCell(value: string | number): string {
  const text = /^[=+\-@\t\r]/.test(String(value)) ? `'${String(value)}` : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const CSV_HEAD = ['district', 'teacher', 'sub', 'schools', 'students', 'current_benchmark', 'current_window', 'flashlight_students', 'problem_students', 'verdict', 'note', 'problems'];

/** The stored sample of a teacher's problems, saying how many more there are. */
export function problemLines(row: Pick<AuditTeacherResult, 'problems' | 'problemStudents'>): string[] {
  const lines = row.problems.map(describeProblem);
  const more = row.problemStudents - row.problems.length;
  return more > 0 ? [...lines, `and ${more} more not stored`] : lines;
}

export function readinessCsv(district: Pick<AuditDistrict, 'districtName'> & { readiness?: AuditTeacherReadiness }): string {
  const r = district.readiness;
  const rows = (r?.teachers ?? []).map((t) =>
    [
      district.districtName,
      t.name,
      t.sub,
      t.schools.join('; '),
      t.students,
      t.currentBenchmark,
      t.currentWindow ?? '',
      t.flashlightStudents ?? '',
      t.problemStudents,
      t.verdict,
      verdictNote(t, r?.currentWindow),
      problemLines(t).join(' | '),
    ]
      .map(csvCell)
      .join(','),
  );
  return [CSV_HEAD.join(','), ...rows].join('\n');
}
